import React, {useEffect} from 'react';
import {BrowserRouter, Routes, Route, Navigate, useLocation} from 'react-router-dom';
import {View, ActivityIndicator, StyleSheet} from 'react-native';
import {useAppDispatch, useAppSelector} from '../store';
import {initializeAuth} from '../store/authSlice';
import {colors} from '../constants';

// Web-specific globals
declare const document: {
  getElementById: (id: string) => {style: {display: string}} | null;
};

// Screens
import HomeScreen from '../screens/HomeScreen';
import FrameScreen from '../screens/FrameScreen';
import SettingsScreen from '../screens/SettingsScreen';
import GallerySettingsScreen from '../screens/GallerySettingsScreen';
import CalendarSettingsScreen from '../screens/CalendarSettingsScreen';
import AccountSettingsScreen from '../screens/AccountSettingsScreen';
import DisplaysListScreen from '../screens/DisplaysListScreen';
import DisplayEditScreen from '../screens/DisplayEditScreen';
import DisplayPhotosScreen from '../screens/DisplayPhotosScreen';
import DisplayCalendarsScreen from '../screens/DisplayCalendarsScreen';
import VerifyEmailScreen from '../screens/VerifyEmailScreen';
import MagicLinkScreen from '../screens/MagicLinkScreen';
import QRScanScreen from '../screens/QRScanScreen';
import LegalScreen from '../screens/LegalScreen';
import DonationScreen from '../screens/DonationScreen';
import AccountDeleteScreen from '../screens/AccountDeleteScreen';

export type RootStackParamList = {
  Home: undefined;
  Frame: {displayId?: string};
  Settings: undefined;
  GallerySettings: undefined;
  CalendarSettings: undefined;
  AccountSettings: undefined;
  Displays: undefined;
  DisplayEdit: {displayId: string};
  DisplayPhotos: {displayId: string};
  DisplayCalendars: {displayId: string};
  QRScan: undefined;
  MagicLinkConfirm: {token: string};
  Donation: undefined;
};

function AuthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<FrameScreen />} />
      <Route path="/frame" element={<FrameScreen />} />
      <Route path="/settings" element={<SettingsScreen />} />
      <Route path="/settings/gallery" element={<GallerySettingsScreen />} />
      <Route path="/settings/calendar" element={<CalendarSettingsScreen />} />
      <Route path="/settings/account" element={<AccountSettingsScreen />} />
      <Route path="/settings/displays" element={<DisplaysListScreen />} />
      <Route path="/settings/displays/:displayId" element={<DisplayEditScreen />} />
      <Route path="/settings/displays/:displayId/photos" element={<DisplayPhotosScreen />} />
      <Route path="/settings/displays/:displayId/calendars" element={<DisplayCalendarsScreen />} />
      <Route path="/settings/donate" element={<DonationScreen />} />
      <Route path="/auth/verify-email" element={<VerifyEmailScreen />} />
      <Route path="/account/delete" element={<AccountDeleteScreen />} />
      {/* Legal pages - accessible to all users */}
      <Route path="/legal" element={<Navigate to="/terms" replace />} />
      <Route path="/privacy" element={<LegalScreen />} />
      <Route path="/terms" element={<LegalScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Manages visibility of the static HTML legal footer based on current route
function LegalFooterManager({isAuthenticated}: {isAuthenticated: boolean}) {
  const location = useLocation();

  useEffect(() => {
    const footer = document.getElementById('legal-footer');
    if (footer) {
      // Only show footer on unauthenticated home page
      const isHomePage = location.pathname === '/' || location.pathname === '';
      footer.style.display = !isAuthenticated && isHomePage ? 'block' : 'none';
    }
  }, [location.pathname, isAuthenticated]);

  return null;
}

function UnauthenticatedRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeScreen />} />
      <Route path="/auth/verify-email" element={<VerifyEmailScreen />} />
      <Route path="/auth/magic-link" element={<MagicLinkScreen />} />
      <Route path="/auth/qr-scan" element={<QRScanScreen />} />
      <Route path="/account/delete" element={<AccountDeleteScreen />} />
      {/* Legal pages - accessible to all users */}
      <Route path="/legal" element={<Navigate to="/terms" replace />} />
      <Route path="/privacy" element={<LegalScreen />} />
      <Route path="/terms" element={<LegalScreen />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function AppNavigator() {
  const dispatch = useAppDispatch();
  const {isInitialized, isAuthenticated, isLoading} = useAppSelector(
    state => state.auth,
  );

  useEffect(() => {
    dispatch(initializeAuth());
  }, [dispatch]);

  if (!isInitialized || isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  return (
    <BrowserRouter>
      <LegalFooterManager isAuthenticated={isAuthenticated} />
      {isAuthenticated ? <AuthenticatedRoutes /> : <UnauthenticatedRoutes />}
    </BrowserRouter>
  );
}

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background,
  },
});
